/**
 * Small color utilities used to render room accents.
 *
 * Room colors are arbitrary hex values chosen by admins, so every surface that
 * paints with them must derive a readable foreground instead of assuming white.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

const FALLBACK: Rgb = { r: 37, g: 61, b: 114 }; // brand-700

const clamp = (value: number, min = 0, max = 255): number =>
  Math.min(max, Math.max(min, Math.round(value)));

/** Accepts `#rgb`, `#rrggbb` and bare `rrggbb`. Returns the brand color if invalid. */
export function hexToRgb(hex: string | null | undefined): Rgb {
  if (!hex) return FALLBACK;
  const value = hex.trim().replace(/^#/, '');
  const expanded =
    value.length === 3
      ? value
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : value;

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return FALLBACK;

  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const part = (value: number) => clamp(value).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** `rgb(37 61 114 / 0.12)` — safe to drop straight into an inline style. */
export function withAlpha(hex: string | null | undefined, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${r} ${g} ${b} / ${Math.min(1, Math.max(0, alpha))})`;
}

/** WCAG relative luminance. */
export function relativeLuminance(color: Rgb): number {
  const channel = (value: number): number => {
    const srgb = value / 255;
    return srgb <= 0.04045 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [light, dark] = la >= lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const NEAR_BLACK: Rgb = { r: 27, g: 32, b: 48 }; // surface-900

/** Picks the foreground (white or near-black) with the better contrast. */
export function readableTextColor(hex: string | null | undefined): string {
  const background = hexToRgb(hex);
  return contrastRatio(background, WHITE) >= contrastRatio(background, NEAR_BLACK)
    ? '#ffffff'
    : rgbToHex(NEAR_BLACK);
}

/** Darkens a color toward black by `amount` (0..1). Useful for text on tints. */
export function darken(hex: string | null | undefined, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const factor = 1 - Math.min(1, Math.max(0, amount));
  return rgbToHex({ r: r * factor, g: g * factor, b: b * factor });
}

/** Mixes a color toward white by `amount` (0..1). */
export function lighten(hex: string | null | undefined, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const ratio = Math.min(1, Math.max(0, amount));
  return rgbToHex({
    r: r + (255 - r) * ratio,
    g: g + (255 - g) * ratio,
    b: b + (255 - b) * ratio,
  });
}

/**
 * Flattens a translucent colour over an opaque one, the way the browser paints
 * it. Needed because contrast is a property of what the eye receives, not of
 * the declared colour.
 */
export function compositeOver(hex: string | null | undefined, alpha: number, backdrop: Rgb): Rgb {
  const { r, g, b } = hexToRgb(hex);
  const a = Math.min(1, Math.max(0, alpha));
  return {
    r: r * a + backdrop.r * (1 - a),
    g: g * a + backdrop.g * (1 - a),
    b: b * a + backdrop.b * (1 - a),
  };
}

/**
 * Returns a variant of the room colour that reaches 4.5:1 against the surface
 * it is actually painted on.
 *
 * `backdrop` defaults to white, but callers that tint the background with the
 * same colour must pass the composited surface: a monogram drawn over
 * `withAlpha(color, 0.14)` sits on a pale wash of its own hue, not on white,
 * and measuring against white overstates the contrast by roughly half a point
 * — enough to fail WCAG 1.4.3 while the check reports success.
 */
export function accessibleOnLight(
  hex: string | null | undefined,
  backdrop: Rgb = WHITE,
): string {
  let current = hexToRgb(hex);
  let attempts = 0;
  while (contrastRatio(current, backdrop) < 4.5 && attempts < 24) {
    const next = hexToRgb(darken(rgbToHex(current), 0.12));
    // Pure black is the darkest we can get; stop instead of spinning.
    if (next.r === current.r && next.g === current.g && next.b === current.b) break;
    current = next;
    attempts += 1;
  }
  return rgbToHex(current);
}

/** Unicode combining diacritical marks (U+0300–U+036F). */
const COMBINING_MARKS = /[̀-ͯ]/g;

/**
 * Two letter monogram derived from a name, used when a room or user has no
 * image. Accents are stripped so `Sala Ejecutiva` renders as `SE`.
 */
export function monogramFrom(name: string, maxLength = 2): string {
  const normalized = name.normalize('NFD').replace(COMBINING_MARKS, '').trim();

  if (!normalized) return '?';

  const words = normalized.split(/\s+/).filter((word) => /[a-zA-Z0-9]/.test(word));
  if (words.length === 0) return normalized.slice(0, maxLength).toUpperCase();

  if (words.length === 1) {
    const [only] = words;
    return (only ?? '').slice(0, maxLength).toUpperCase();
  }

  return words
    .slice(0, maxLength)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}
